import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './AnotherOnePiece.css'; // Reuse same CSS
import animeConfig from '../config/animeConfig';
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";

// Leaderboard imports
import { getCast } from '../data/characterCast';
import { saveScore } from '../services/leaderboardService';
import { recordPlay } from '../services/playHistory';
import { useUsername } from '../hooks/useUsername';
import UsernameModal from '../components/UsernameModal/UsernameModal';
import QuizPageText from '../components/QuizPageText/QuizPageText';
import { imageQuizText } from '../components/QuizPageText/quizPageText.data';

// Analytics
import { trackQuizStart, trackQuizComplete } from '../services/analyticsService';
import useGameFullscreen from '../hooks/useGameFullscreen';

const OnePieceImageQuiz = ({ animeIds, theme }) => {
  const navigate = useNavigate();
  const { username, avatar, hasUsername, register } = useUsername();
  const config = animeConfig[theme];
  
  const [characters, setCharacters] = useState([]);
  const [animeData, setAnimeData] = useState({});
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [isAnswered, setIsAnswered] = useState(false);
  const [choices, setChoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [incorrectAnswers, setIncorrectAnswers] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  useGameFullscreen(gameStarted && !gameOver);
  const [passesUsed, setPassesUsed] = useState(0);
  const [questionsLeft, setQuestionsLeft] = useState(15);
  const [answeredQuestions, setAnsweredQuestions] = useState(0);
  const [introScreen, setIntroScreen] = useState(true);
  const [timeLeft, setTimeLeft] = useState(15);

  // Leaderboard states
  const [gameStartTime, setGameStartTime] = useState(null);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [pendingLeaderboardSave, setPendingLeaderboardSave] = useState(null);

  const maxQuestions = 15;
  const maxPasses = 6;

  const backgroundMusic = useRef(null);

  useEffect(() => {
    if (backgroundMusic.current) {
      backgroundMusic.current.pause();
      backgroundMusic.current.currentTime = 0;
    }
    backgroundMusic.current = new Audio(config.backgroundMusic);
    backgroundMusic.current.loop = true;
    backgroundMusic.current.onerror = () => {
      console.error('Failed to load background music:', config.backgroundMusic);
    };
  }, [config.backgroundMusic]);

  useEffect(() => {
    if (timeLeft > 0 && !isAnswered && gameStarted && !gameOver) {
      const timer = setTimeout(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !isAnswered) {
      handleTimeout();
    }
  }, [timeLeft, isAnswered, gameStarted, gameOver]);

  const handleTimeout = () => {
    setIncorrectAnswers((prev) => prev + 1);
    setAnsweredQuestions((prev) => prev + 1);
    setQuestionsLeft((prev) => prev - 1);
    setIsAnswered(true);

    if (answeredQuestions + 1 === maxQuestions) {
      setGameOver(true);
    } else {
      setTimeout(() => nextQuestion(), 1000);
    }
  };

  const incorrectSound = new Audio(config.incorrectSound);
  // One save per round. Registering a name logs the player in, which changes
  // hasUsername/username — deps of the game-over effect below — so without this
  // the same round was saved a second time. (BountyBattle already guards this way.)
  const savedThisRound = useRef(false);

  const gameOverSound = useRef(new Audio(config.gameOverSound));
  gameOverSound.current.loop = true;
  const correctAnswerSound = new Audio(config.correctAnswerSound);

  const fetchGameData = async (animeId) => {
    try {
      // Cast from the bundled list — see src/data/characterCast.js for why this
      // is no longer fetched.
      setCharacters(getCast());
      setLoading(false);

      const animeResponse = await fetch(`https://api.jikan.moe/v4/anime/${animeId}`);
      const animeDataResponse = await animeResponse.json();
      setAnimeData(animeDataResponse.data);
    } catch (error) {
      console.error('Error fetching optional anime data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (animeIds && animeIds.length > 0) {
      const randomAnimeId = animeIds[Math.floor(Math.random() * animeIds.length)];
      fetchGameData(randomAnimeId);
    }
  }, [animeIds]);

  const shuffleCharacters = (characters) => {
    let shuffled = [...characters];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const shuffledCharacters = useMemo(() => {
    return shuffleCharacters(characters);
  }, [characters]);

  useEffect(() => {
    if (shuffledCharacters && shuffledCharacters.length > 0) {
      setChoices(getRandomImageChoices());
    }
  }, [shuffledCharacters, questionIndex]);

  // NEW: Get random image choices instead of name choices
  const getRandomImageChoices = () => {
    const correctCharacter = shuffledCharacters[questionIndex];
    if (!correctCharacter) return [];

    const allCharacters = shuffledCharacters.filter(Boolean);
    const filteredCharacters = allCharacters.filter((c) => c !== correctCharacter);

    let randomCharacters = filteredCharacters.sort(() => 0.5 - Math.random()).slice(0, 3);
    const finalChoices = [correctCharacter, ...randomCharacters].sort(() => Math.random() - 0.5);

    return finalChoices.slice(0, 4);
  };

  const handleAnswer = (selectedCharacter) => {
    const correctCharacter = shuffledCharacters[questionIndex];
    const isCorrect = selectedCharacter?.character?.mal_id === correctCharacter?.character?.mal_id;
    
    const updatedCorrect = isCorrect ? correctAnswers + 1 : correctAnswers;
    const updatedIncorrect = !isCorrect ? incorrectAnswers + 1 : incorrectAnswers;
    const updatedAnswered = answeredQuestions + 1;

    setSelectedAnswer(selectedCharacter);
    setIsAnswered(true);

    if (!isCorrect) {
      incorrectSound.play();
      setIncorrectAnswers(updatedIncorrect);
    }

    if (isCorrect) {
      correctAnswerSound.play();
      setScore(score + 1);
      setCorrectAnswers(updatedCorrect);
    }

    if (!gameStarted) {
      setGameStarted(true);
    }

    setAnsweredQuestions(updatedAnswered);
    setQuestionsLeft(maxQuestions - updatedAnswered);

    if (updatedCorrect + updatedIncorrect === maxQuestions || passesUsed === maxPasses) {
      setGameOver(true);
    }

    setTimeLeft(15);
  };

  const handlePass = () => {
    if (passesUsed < maxPasses) {
      setPassesUsed(passesUsed + 1);
      nextQuestion();
    }
  };

  const restartCurrentGame = () => {
    setIntroScreen(true);
    setGameStarted(false);
    setGameOver(false);
    savedThisRound.current = false;
    setScore(0);
    setCorrectAnswers(0);
    setIncorrectAnswers(0);
    setQuestionIndex(0);
    setAnsweredQuestions(0);
    setQuestionsLeft(maxQuestions);
    setPassesUsed(0);
    setTimeLeft(15);
    setGameStartTime(null); // Reset game start time
    setShowUsernameModal(false);
    setPendingLeaderboardSave(null);
    gameOverSound.current.pause();
    gameOverSound.current.currentTime = 0;
    backgroundMusic.current.pause();
    backgroundMusic.current.currentTime = 0;
    fetchGameData(animeIds[Math.floor(Math.random() * animeIds.length)]);
  };

  // Handle username submission from modal (with password and avatar for new registration)
  const handleUsernameSubmit = async (newUsername, password, selectedAvatar, email) => {
    const result = await register(newUsername, password, selectedAvatar, email);
    if (result.success && pendingLeaderboardSave) {
      await saveScore({ ...pendingLeaderboardSave, username: newUsername, avatar: selectedAvatar });
      setShowUsernameModal(false);
      setPendingLeaderboardSave(null);
    } else if (!result.success) {
      throw new Error(result.error);
    }
  };

  // Handle skip - don't save score to leaderboard
  const handleUsernameSkip = () => {
    setShowUsernameModal(false);
    setPendingLeaderboardSave(null); // Discard the pending score
  };

  const nextQuestion = () => {
    setQuestionIndex(questionIndex + 1);
    setIsAnswered(false);
    setSelectedAnswer(null);
    setTimeLeft(15);
  };

  const handleStartGame = () => {
    setIntroScreen(false);
    setGameStarted(true);
    setGameStartTime(Date.now()); // Track game start time for leaderboard
    trackQuizStart('image'); // Analytics tracking
    backgroundMusic.current.play().catch((err) => {
      console.error('Autoplay prevented:', err);
    });
  };

  useEffect(() => {
    if (gameOver && !savedThisRound.current) {
      savedThisRound.current = true;
      // Calculate total time
      const totalTime = gameStartTime ? Math.floor((Date.now() - gameStartTime) / 1000) : 0;

      // Analytics tracking
      trackQuizComplete('image', score, 15, totalTime);

      // Save game result to Firestore (existing functionality)
      const firebaseResult = {
        score,
        correctAnswers,
        incorrectAnswers,
        date: new Date().toISOString(),
        theme: theme + "_image_quiz",
      };

      const saveGameResult = async () => {
        try {
          const resultsRef = collection(db, "users", "guest_user", "gameResults");
          await addDoc(resultsRef, firebaseResult);
          console.log(`Game result saved to Firebase for ${theme} image quiz:`, firebaseResult);
        } catch (error) {
          console.error(`Error saving game result to Firebase for ${theme}:`, error);
        }
      };

      saveGameResult();

      // Prepare leaderboard entry
      const leaderboardEntry = {
        score,
        correctAnswers,
        incorrectAnswers,
        totalTime,
        gameType: 'image',
        date: new Date().toISOString(),
      };
      recordPlay(leaderboardEntry);

      // If user has username, save immediately
      // Otherwise, show modal to collect username
      if (hasUsername && username) {
        saveScore({ ...leaderboardEntry, username, avatar }).then((result) => {
          console.log('Leaderboard save result:', result);
        });
      } else {
        setPendingLeaderboardSave(leaderboardEntry);
        setShowUsernameModal(true);
      }

      // Play game-over sound and stop background music
      gameOverSound.current.play();
      backgroundMusic.current.pause();
    }
  }, [gameOver, theme, score, correctAnswers, incorrectAnswers, gameStartTime, hasUsername, username, avatar]);

  if (loading) {
    return <p>Loading game data...</p>;
  }

  const currentCharacter = shuffledCharacters[questionIndex];
  const characterName = currentCharacter?.character?.name || 'Unknown';

  return (
    <>
    <Helmet>
      <title>One Piece Image Quiz - Pick the Right Character | One Piece Trivia</title>
      <meta name="description" content="Pick the right One Piece character from four images. 15 rounds, timed, with a leaderboard. Play free in your browser." />
      <link rel="canonical" href="https://www.onepiecetrivia.com/image-quiz" />
      <meta property="og:title" content="One Piece Image Quiz - Pick the Right Character" />
      <meta property="og:description" content="Pick the right One Piece character from four images. 15 rounds, timed, with a leaderboard." />
      <meta property="og:url" content="https://www.onepiecetrivia.com/image-quiz" />
      <meta name="twitter:title" content="One Piece Image Quiz - Pick the Right Character" />
      <meta name="twitter:description" content="Pick the right One Piece character from four images. 15 rounds, timed, with a leaderboard." />
      <script type="application/ld+json">{`{
        "@context": "https://schema.org",
        "@type": "Quiz",
        "name": "One Piece Image Quiz",
        "description": "Pick the right One Piece character from four images. 15 rounds, timed, with a leaderboard.",
        "url": "https://www.onepiecetrivia.com/image-quiz",
        "about": { "@type": "Thing", "name": "One Piece Anime" },
        "educationalLevel": "beginner",
        "numberOfQuestions": 15,
        "isAccessibleForFree": true,
        "inLanguage": "en"
      }`}</script>
    </Helmet>
    <div
      className="new-onepiece-game-container"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.9)), url(${config.backgroundImage})`,
      }}
    >
{introScreen ? (
        <div className="intro-screen">
          <h1 className="new-onepiece-intro-title" style={{ fontSize: '2rem', marginBottom: '20px' }}>One Piece Image Quiz</h1>
          <h3 style={{ color: '#ffd700', fontSize: '1.3rem', marginBottom: '30px', textAlign: 'center', padding: '0 20px' }}>
            SEE THE NAME - PICK THE RIGHT CHARACTER IMAGE!
          </h3>
          <div className="intro-image-container">
            <img src={config.introImage} alt="Game Start" className="intro-image" />
          </div>
          <button className="start-game-button" onClick={handleStartGame}>
            <span className="start-icon">🎮</span> Start Game
          </button>
        </div>
      ) : (
        <div className="new-onepiece-content">
          <p>Anime: {animeData?.title || 'Loading...'}</p>
          {gameStarted && !gameOver && (
            <div className="new-onepiece-game-info-container">
              <div className="new-onepiece-top-info-game">
                <div className="info-row">
                  <div className="info-box">
                    <p>{questionsLeft} Questions Left</p>
                  </div>
                  <div className="info-box">
                    <p>Your Score: {score}</p>
                  </div>
                </div>
                <div className="info-row">
                  <div className="info-box">
                    <p>Correct: {correctAnswers}</p>
                  </div>
                  <div className="info-box">
                    <p>Incorrect: {incorrectAnswers}</p>
                  </div>
                </div>
                <div className="info-row">
                  <div className="info-box">
                    <p>Passes Remaining: {maxPasses - passesUsed}/{maxPasses}</p>
                  </div>
                  <div className="info-box timer-container">
                    <p className={`timer-display ${timeLeft <= 5 ? 'timer-warning' : ''}`}>
                      Time Left: {timeLeft}s
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

{/* Show CHARACTER NAME instead of image */}
<div className="new-onepiece-character-image-container" style={{ minHeight: '60px', padding: '10px 0' }}>
            <h2 style={{ color: 'white', fontSize: '1.1rem', textAlign: 'center', padding: '10px 15px', fontWeight: '600', margin: '0' }}>
              {characterName}
            </h2>
          </div>

          {!gameOver && (
            <div className="new-onepiece-choices-container">
              {/* Show 4 CHARACTER IMAGES as choices */}
              <div className="choice-row">
                {choices.slice(0, 2).map((char, index) => (
                  <button
                    key={index}
                    className={`new-onepiece-choice-button ${
                      selectedAnswer?.character?.mal_id === char?.character?.mal_id
                        ? selectedAnswer?.character?.mal_id === currentCharacter?.character?.mal_id
                          ? 'correct'
                          : 'incorrect'
                        : isAnswered && char?.character?.mal_id === currentCharacter?.character?.mal_id
                        ? 'correct'
                        : ''
                    }`}
                    onClick={() => handleAnswer(char)}
                    disabled={isAnswered}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '8px',
                      minHeight: '150px',
                      border: isAnswered && char?.character?.mal_id === currentCharacter?.character?.mal_id ? '3px solid #00ff00' : '',
                    }}
                  >
                    <img
                      src={char?.character?.images?.jpg?.image_url || 'https://via.placeholder.com/150'}
                      alt={char?.character?.name}
                      style={{
                        width: '90px',
                        height: '110px',
                        objectFit: 'cover',
                        borderRadius: '6px',
                        marginBottom: '5px',
                      }}
                    />
                    {selectedAnswer?.character?.mal_id === char?.character?.mal_id &&
                      selectedAnswer?.character?.mal_id === currentCharacter?.character?.mal_id && (
                        <span className="correct-icon">✔️</span>
                      )}
                    {selectedAnswer?.character?.mal_id === char?.character?.mal_id &&
                      selectedAnswer?.character?.mal_id !== currentCharacter?.character?.mal_id && (
                        <span className="incorrect-icon">❌</span>
                      )}
                  </button>
                ))}
              </div>
              <div className="choice-row">
                {choices.slice(2, 4).map((char, index) => (
                  <button
                    key={index + 2}
                    className={`new-onepiece-choice-button ${
                      selectedAnswer?.character?.mal_id === char?.character?.mal_id
                        ? selectedAnswer?.character?.mal_id === currentCharacter?.character?.mal_id
                          ? 'correct'
                          : 'incorrect'
                        : isAnswered && char?.character?.mal_id === currentCharacter?.character?.mal_id
                        ? 'correct'
                        : ''
                    }`}
                    onClick={() => handleAnswer(char)}
                    disabled={isAnswered}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '8px',
                      minHeight: '150px',
                      border: isAnswered && char?.character?.mal_id === currentCharacter?.character?.mal_id ? '3px solid #00ff00' : '',
                    }}
                  >
                    <img
                      src={char?.character?.images?.jpg?.image_url || 'https://via.placeholder.com/150'}
                      alt={char?.character?.name}
                      style={{
                        width: '90px',
                        height: '110px',
                        objectFit: 'cover',
                        borderRadius: '6px',
                        marginBottom: '5px',
                      }}
                    />
                    {selectedAnswer?.character?.mal_id === char?.character?.mal_id &&
                      selectedAnswer?.character?.mal_id === currentCharacter?.character?.mal_id && (
                        <span className="correct-icon">✔️</span>
                      )}
                    {selectedAnswer?.character?.mal_id === char?.character?.mal_id &&
                      selectedAnswer?.character?.mal_id !== currentCharacter?.character?.mal_id && (
                        <span className="incorrect-icon">❌</span>
                      )}
                  </button>
                ))}
              </div>
              <div className="pass-button-container">
                <button className="pass-button" onClick={handlePass} disabled={passesUsed >= maxPasses || isAnswered}>
                  Pass
                </button>
              </div>
            </div>
          )}

          {isAnswered && (
            <div className="new-onepiece-answer-feedback">
              {selectedAnswer?.character?.mal_id === currentCharacter?.character?.mal_id ? (
                <p className="new-onepiece-correct-answer">Correct!</p>
              ) : (
                <p className="new-onepiece-incorrect-answer">
                  Incorrect! The correct answer is {currentCharacter?.character?.name}
                </p>
              )}
              <button
                onClick={nextQuestion}
                className={`new-onepiece-next-button ${
                  isAnswered && selectedAnswer?.character?.mal_id === currentCharacter?.character?.mal_id ? 'correct' : 'incorrect'
                }`}
              >
                {questionIndex + 1 >= maxQuestions ? '> ' : '>'}
              </button>
            </div>
          )}

          {gameOver && (
            <div
              className="new-onepiece-game-over"
              style={{
                backgroundImage: `url(${config.gameOverBackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
              }}
            >
              <h3 className="game-over-heading">Game Over!</h3>
              <div className="game-results">
                <p className="result correct">Correct Answers: {correctAnswers}</p>
                <p className="result incorrect">Incorrect Answers: {incorrectAnswers}</p>
              </div>
              <div className="new-onepiece-button-container">
                <button onClick={restartCurrentGame} className="new-onepiece-rematch-button">
                  Start Again
                </button>
                <button onClick={() => navigate('/leaderboard')} className="new-onepiece-leaderboard-button">
                  View Leaderboard
                </button>
              </div>
            </div>
          )}

          {!gameOver && gameStarted && (
            <div className="game-action-buttons-container">
              <div className="action-button-row">
                <button onClick={restartCurrentGame} className="restart-game-button">
                  Restart Game
                </button>
                {isAnswered && (
                  <button onClick={nextQuestion} className="next-question-button">
                    Next Question
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Username Modal for leaderboard */}
      <UsernameModal
        isOpen={showUsernameModal}
        standing={pendingLeaderboardSave && { gameType: 'image', score: pendingLeaderboardSave.score, totalTime: pendingLeaderboardSave.totalTime, max: 15 }}
        onSubmit={handleUsernameSubmit}
        onSkip={handleUsernameSkip}
        initialUsername=""
      />
    </div>
      <QuizPageText {...imageQuizText} />
    </>
  );
};

export default OnePieceImageQuiz;
